import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, X, Star, UserMinus, UserPlus, Camera } from 'lucide-react';
import { Input, FormGroup } from '../components/Form';
import {
    getSquads,
    criarSquad,
    desativarSquad,
    getPessoasSemSquad,
    adicionarMembro,
    removerMembro,
    setSquadLeader,
    uploadSquadFoto,
} from '../services/squads';

export default function Squads({ embedded = false }) {
    const [squads, setSquads] = useState([]);
    const [pessoasSemSquad, setPessoasSemSquad] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Modal criar
    const [modalCriar, setModalCriar] = useState(false);
    const [formNome, setFormNome] = useState('');
    const [saving, setSaving] = useState(false);

    // Modal detalhe
    const [squadDetalhe, setSquadDetalhe] = useState(null);
    const [membroParaAdicionar, setMembroParaAdicionar] = useState('');
    const [addingMembro, setAddingMembro] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [togglingLeaderId, setTogglingLeaderId] = useState(null);
    const [desativando, setDesativando] = useState(false);

    // Foto upload
    const fotoInputRef = useRef(null);
    const [uploadingFoto, setUploadingFoto] = useState(false);
    const [fotoPreview, setFotoPreview] = useState(null);

    const loadSquads = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getSquads();
            setSquads(data);
        } catch {
            setError('Erro ao carregar squads.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSquads();
    }, [loadSquads]);

    const showSuccess = (msg) => {
        setSuccess(msg);
        setTimeout(() => setSuccess(null), 3000);
    };

    // ── Criar squad ─────────────────────────────────────────────────────────────

    const abrirModalCriar = () => {
        setFormNome('');
        setError(null);
        setModalCriar(true);
    };

    const handleCriar = async (e) => {
        e.preventDefault();
        if (!formNome.trim()) return;
        setSaving(true);
        setError(null);
        try {
            await criarSquad(formNome);
            setModalCriar(false);
            showSuccess('Squad criado com sucesso.');
            await loadSquads();
        } catch (err) {
            setError(err.message || 'Erro ao criar squad.');
        } finally {
            setSaving(false);
        }
    };

    // ── Modal detalhe ────────────────────────────────────────────────────────────

    const abrirDetalhe = async (squad) => {
        setSquadDetalhe(squad);
        setMembroParaAdicionar('');
        setFotoPreview(squad.foto_url || null);
        setError(null);
        try {
            const disponiveis = await getPessoasSemSquad();
            setPessoasSemSquad(disponiveis);
        } catch {
            setPessoasSemSquad([]);
        }
    };

    const fecharDetalhe = () => {
        setSquadDetalhe(null);
        setFotoPreview(null);
        setError(null);
    };

    const handleFotoChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !squadDetalhe) return;
        setError(null);
        // Preview local imediato
        const objectUrl = URL.createObjectURL(file);
        setFotoPreview(objectUrl);
        setUploadingFoto(true);
        try {
            const publicUrl = await uploadSquadFoto(squadDetalhe.id, file);
            setFotoPreview(publicUrl);
            // Atualiza lista
            const updated = await getSquads();
            setSquads(updated);
            setSquadDetalhe(updated.find(s => s.id === squadDetalhe.id) || null);
            showSuccess('Foto atualizada com sucesso.');
        } catch (err) {
            setError(err.message || 'Erro ao fazer upload da foto.');
            setFotoPreview(squadDetalhe.foto_url || null);
        } finally {
            setUploadingFoto(false);
            if (fotoInputRef.current) fotoInputRef.current.value = '';
        }
    };

    const handleAdicionarMembro = async () => {
        if (!membroParaAdicionar || !squadDetalhe) return;
        setAddingMembro(true);
        setError(null);
        try {
            await adicionarMembro(membroParaAdicionar, squadDetalhe.id);
            showSuccess('Membro adicionado.');
            const [updated, disponiveis] = await Promise.all([getSquads(), getPessoasSemSquad()]);
            setSquads(updated);
            setPessoasSemSquad(disponiveis);
            setSquadDetalhe(updated.find(s => s.id === squadDetalhe.id) || null);
            setMembroParaAdicionar('');
        } catch (err) {
            setError(err.message || 'Erro ao adicionar membro.');
        } finally {
            setAddingMembro(false);
        }
    };

    const handleRemoverMembro = async (pessoaId) => {
        setRemovingId(pessoaId);
        setError(null);
        try {
            await removerMembro(pessoaId);
            showSuccess('Membro removido.');
            const [updated, disponiveis] = await Promise.all([getSquads(), getPessoasSemSquad()]);
            setSquads(updated);
            setPessoasSemSquad(disponiveis);
            setSquadDetalhe(updated.find(s => s.id === squadDetalhe.id) || null);
        } catch (err) {
            setError(err.message || 'Erro ao remover membro.');
        } finally {
            setRemovingId(null);
        }
    };

    const handleToggleLeader = async (pessoaId, isCurrentLeader) => {
        setTogglingLeaderId(pessoaId);
        setError(null);
        try {
            await setSquadLeader(squadDetalhe.id, pessoaId, !isCurrentLeader);
            const updated = await getSquads();
            setSquads(updated);
            setSquadDetalhe(updated.find(s => s.id === squadDetalhe.id) || null);
        } catch (err) {
            setError(err.message || 'Erro ao atualizar leader.');
        } finally {
            setTogglingLeaderId(null);
        }
    };

    const handleDesativar = async () => {
        if (!squadDetalhe) return;
        setDesativando(true);
        setError(null);
        try {
            await desativarSquad(squadDetalhe.id);
            showSuccess('Squad desativado.');
            fecharDetalhe();
            await loadSquads();
        } catch (err) {
            setError(err.message || 'Erro ao desativar squad.');
        } finally {
            setDesativando(false);
        }
    };

    // ── Render helpers ───────────────────────────────────────────────────────────

    const membros = squadDetalhe?.bd_pessoas || [];

    return (
        <div>
            {!embedded && (
                <div className="page-header">
                    <div className="page-header-left">
                        <div className="page-kicker">Time Interno</div>
                        <h1 className="page-title">Squads</h1>
                    </div>
                    <div className="page-header-right">
                        <button className="pill pill-primary pill-sm" onClick={abrirModalCriar}>
                            <Plus size={14} /> Novo Squad
                        </button>
                    </div>
                </div>
            )}

            <div className="page-body" style={embedded ? { minHeight: 'auto' } : undefined}>
                {success && <div className="alert alert-success">{success}</div>}
                {error && !modalCriar && !squadDetalhe && (
                    <div className="alert alert-error">{error}</div>
                )}

                {loading ? (
                    <div className="loading-state">Carregando squads…</div>
                ) : squads.length === 0 ? (
                    <div className="empty-state">Nenhum squad cadastrado.</div>
                ) : (
                    <div className="card">
                        <p className="section-label">Squads ({squads.length})</p>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {squads.map((squad, i) => {
                                const membrosSquad = squad.bd_pessoas || [];
                                const leaderSquad = membrosSquad.find(m => m.squad_leader);
                                return (
                                    <div
                                        key={squad.id}
                                        onClick={() => abrirDetalhe(squad)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '0.875rem 0',
                                            borderBottom: i < squads.length - 1 ? '1px solid var(--glass-border)' : 'none',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                                            {squad.foto_url ? (
                                                <img
                                                    src={squad.foto_url}
                                                    alt={squad.nome}
                                                    style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid var(--glass-border)' }}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: 34, height: 34, borderRadius: '50%',
                                                    background: 'var(--canvas-2)',
                                                    border: '1px solid var(--glass-border)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '0.8125rem', fontWeight: 700, color: 'var(--emerald)',
                                                    flexShrink: 0,
                                                }}>
                                                    {(squad.nome?.[0] || 'S').toUpperCase()}
                                                </div>
                                            )}
                                            <div>
                                                <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9375rem', color: 'var(--ink)' }}>
                                                    {squad.nome}
                                                </p>
                                                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--muted)' }}>
                                                    {membrosSquad.length} {membrosSquad.length === 1 ? 'membro' : 'membros'}
                                                    {leaderSquad && (
                                                        <span style={{ marginLeft: '0.5rem' }}>
                                                            · <Star size={10} fill="currentColor" style={{ color: 'var(--yellow, #f59e0b)', verticalAlign: 'middle' }} /> {leaderSquad.nome}
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <span className={`chip ${squad.ativo ? 'chip-active' : 'chip-muted'}`} style={{ fontSize: '0.7rem' }}>
                                            {squad.ativo ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* ── Modal criar squad ─────────────────────────────────────────────── */}
            {modalCriar && (
                <div className="modal-overlay" onClick={() => setModalCriar(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <span>Novo Squad</span>
                            <button className="btn-icon" onClick={() => setModalCriar(false)}>
                                <X size={16} />
                            </button>
                        </div>
                        <form onSubmit={handleCriar}>
                            <div className="modal-body">
                                {error && <div className="alert alert-error">{error}</div>}
                                <FormGroup label="Nome do squad">
                                    <Input
                                        value={formNome}
                                        onChange={e => setFormNome(e.target.value)}
                                        placeholder="Ex: Squad Alpha"
                                        required
                                        autoFocus
                                    />
                                </FormGroup>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="pill pill-sm" onClick={() => setModalCriar(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="pill pill-primary pill-sm" disabled={saving || !formNome.trim()}>
                                    {saving ? 'Salvando…' : 'Criar Squad'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── Modal detalhe squad ───────────────────────────────────────────── */}
            {squadDetalhe && (
                <div className="modal-overlay" onClick={fecharDetalhe}>
                    <div className="modal-content" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                <span>{squadDetalhe.nome}</span>
                                <span className={`chip ${squadDetalhe.ativo ? 'chip-active' : 'chip-muted'}`} style={{ fontSize: '0.7rem' }}>
                                    {squadDetalhe.ativo ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>
                            <button className="btn-icon" onClick={fecharDetalhe}>
                                <X size={16} />
                            </button>
                        </div>

                        <div className="modal-body">
                            {error && <div className="alert alert-error">{error}</div>}

                            {/* Foto do squad */}
                            <div className="section-label">Foto do squad</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem' }}>
                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                    {fotoPreview ? (
                                        <img
                                            src={fotoPreview}
                                            alt={squadDetalhe.nome}
                                            style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--glass-border)', opacity: uploadingFoto ? 0.5 : 1 }}
                                        />
                                    ) : (
                                        <div style={{
                                            width: 64, height: 64, borderRadius: '50%',
                                            background: 'var(--canvas-2)', border: '2px dashed var(--glass-border)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: 'var(--dim)',
                                        }}>
                                            <Camera size={22} />
                                        </div>
                                    )}
                                    {uploadingFoto && (
                                        <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' }}>
                                            <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <input
                                        ref={fotoInputRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleFotoChange}
                                    />
                                    <button
                                        type="button"
                                        className="pill pill-sm"
                                        disabled={uploadingFoto}
                                        onClick={() => fotoInputRef.current?.click()}
                                    >
                                        <Camera size={13} />
                                        {uploadingFoto ? 'Enviando…' : fotoPreview ? 'Trocar foto' : 'Adicionar foto'}
                                    </button>
                                    <p style={{ margin: '0.35rem 0 0', fontSize: '0.72rem', color: 'var(--dim)' }}>
                                        PNG, JPG ou WebP · máx 2 MB
                                    </p>
                                </div>
                            </div>

                            {/* Membros */}
                            <div className="section-label">Membros ({membros.length})</div>
                            {membros.length === 0 ? (
                                <div style={{ fontSize: '0.82rem', color: 'var(--dim)', padding: '0.5rem 0' }}>
                                    Nenhum membro ainda.
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
                                    {membros.map(membro => (
                                        <div
                                            key={membro.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.6rem',
                                                padding: '0.45rem 0.6rem',
                                                background: 'var(--surface-2, var(--surface))',
                                                borderRadius: 6,
                                            }}
                                        >
                                            {/* Avatar */}
                                            <div style={{
                                                width: 28, height: 28, borderRadius: '50%',
                                                background: 'var(--emerald)',
                                                color: '#fff', fontSize: '0.7rem', fontWeight: 700,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                {(membro.nome?.[0] || '?').toUpperCase()}
                                            </div>

                                            {/* Info */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: '0.84rem', fontWeight: 500, lineHeight: 1.2 }}>
                                                    {membro.nome}
                                                </div>
                                                {membro.cargo && (
                                                    <div style={{ fontSize: '0.72rem', color: 'var(--dim)' }}>{membro.cargo}</div>
                                                )}
                                            </div>

                                            {/* Leader toggle */}
                                            <button
                                                className="btn-icon"
                                                title={membro.squad_leader ? 'Remover leader' : 'Definir como leader'}
                                                disabled={togglingLeaderId === membro.id}
                                                onClick={() => handleToggleLeader(membro.id, membro.squad_leader)}
                                                style={{ color: membro.squad_leader ? 'var(--yellow, #f59e0b)' : 'var(--dim)' }}
                                            >
                                                <Star size={15} fill={membro.squad_leader ? 'currentColor' : 'none'} />
                                            </button>

                                            {/* Remove */}
                                            <button
                                                className="btn-icon"
                                                title="Remover do squad"
                                                disabled={removingId === membro.id}
                                                onClick={() => handleRemoverMembro(membro.id)}
                                                style={{ color: 'var(--dim)' }}
                                            >
                                                <UserMinus size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Adicionar membro */}
                            {squadDetalhe.ativo && pessoasSemSquad.length > 0 && (
                                <>
                                    <div className="section-label">Adicionar membro</div>
                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                        <select
                                            className="input"
                                            style={{ flex: 1 }}
                                            value={membroParaAdicionar}
                                            onChange={e => setMembroParaAdicionar(e.target.value)}
                                        >
                                            <option value="">Selecionar pessoa…</option>
                                            {pessoasSemSquad.map(p => (
                                                <option key={p.id} value={p.id}>
                                                    {p.nome}{p.cargo ? ` · ${p.cargo}` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            className="pill pill-primary pill-sm"
                                            disabled={!membroParaAdicionar || addingMembro}
                                            onClick={handleAdicionarMembro}
                                            style={{ flexShrink: 0 }}
                                        >
                                            <UserPlus size={13} />
                                            {addingMembro ? 'Adicionando…' : 'Adicionar'}
                                        </button>
                                    </div>
                                </>
                            )}

                            {squadDetalhe.ativo && pessoasSemSquad.length === 0 && (
                                <div style={{ fontSize: '0.78rem', color: 'var(--dim)', marginTop: '0.5rem' }}>
                                    Todas as pessoas já estão em squads.
                                </div>
                            )}
                        </div>

                        {squadDetalhe.ativo && (
                            <div className="modal-footer" style={{ justifyContent: 'flex-start' }}>
                                <button
                                    className="pill pill-sm"
                                    style={{ color: 'var(--red, #ef4444)' }}
                                    disabled={desativando}
                                    onClick={handleDesativar}
                                >
                                    {desativando ? 'Desativando…' : 'Desativar squad'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
